import type { ComponentType, JSX, MemoExoticComponent } from 'react'
import type { SkinView } from '../../index.h'
import type { Scope } from '../core'
import type { Recursive } from './Recursive'

import { get } from '@setplex/arcana/skin'
import { memo, Suspense } from 'react'
import { COMPONENT, get as getLocal } from '~/core/repository'
import { logger } from '~/shared/logger'
import { key } from '../core'
import { Exact, hasAlias } from './Exact'
import { Fragment, hasComponents } from './Fragment'

interface Props {
  skin: SkinView
  parent: Scope
  Recursive: Recursive
  [key: string]: unknown // any additional props, will be propogated to all children
}

// exports memoized component
export const One: MemoExoticComponent<typeof One_> = memo(One_)

// One_.whyDidYouRender = true
One_.displayName = 'One'
function One_({
  skin,
  parent,
  Recursive,
  ...props
}: Props): JSX.Element | null {
  if (!hasAlias(skin) && hasComponents(skin)) {
    return (
      <Fragment
        {...props}
        key={key(skin)}
        skin={skin}
        parent={parent}
        Recursive={Recursive}
      />
    )
  }

  if (!hasAlias(skin)) {
    logger.warn(`There is no "alias" field, don't know what to render:`, skin)
    return null
  }

  const LocalComponent: ComponentType<unknown> | undefined | null = getLocal(
    skin.alias,
    COMPONENT
  )

  if (LocalComponent) {
    if (!LocalComponent.displayName) {
      LocalComponent.displayName = skin.alias
    }

    return (
      <Exact
        {...props}
        key={key(skin)}
        skin={skin}
        parent={parent}
        Component={LocalComponent}
        Recursive={Recursive}
      />
    )
  }

  const ArcanaComponent: ComponentType<unknown> | undefined = get(skin.alias)

  if (!ArcanaComponent) {
    logger.error(
      `Cannot get component #${skin.id} (${skin.alias}) from arcana library`,
      skin
    )
    return null
  }

  if (!ArcanaComponent.displayName) {
    ArcanaComponent.displayName = skin.alias
  }

  return (
    <Suspense fallback={null}>
      <Exact
        {...props}
        key={key(skin)}
        skin={skin}
        parent={parent}
        Component={ArcanaComponent}
        Recursive={Recursive}
      />
    </Suspense>
  )
}
